// <s-recently-viewed> remembers product handles in localStorage (product pages set
// data-record) and renders the shopper's recent products from Shopify's product
// JSON endpoint. The region stays hidden until there is at least one product to show.
const STORAGE_KEY = 'theme:recently-viewed';
const MAX_STORED = 12;

function readHandles() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
}

function writeHandles(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_STORED)));
  } catch {
    // Storage can be unavailable (private mode); the region simply stays hidden.
  }
}

function formatMoney(cents, format) {
  const amount = Number(cents || 0) / 100;
  const pattern = format || '{{amount}}';
  const withDelimiters = (value, decimals, thousands, decimal) => {
    const [whole, fraction] = value.toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };
  return pattern.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return withDelimiters(amount, 0, ',', '.');
      case 'amount_with_comma_separator':
        return withDelimiters(amount, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(amount, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return withDelimiters(amount, 2, "'", '.');
      default:
        return withDelimiters(amount, 2, ',', '.');
    }
  });
}

class SRecentlyViewed extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const current = this.dataset.record || '';
    let handles = readHandles();

    if (current) {
      handles = [current, ...handles.filter((h) => h !== current)];
      writeHandles(handles);
    }

    const limit = Number.parseInt(this.dataset.limit || '4', 10) || 4;
    const toShow = handles.filter((h) => h !== current).slice(0, limit);
    if (toShow.length === 0) return;

    this.#render(toShow);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #render(handles) {
    const list = this.querySelector('[data-recently-viewed-list]');
    if (!list) return;
    const root = window.Shopify?.routes?.root || '/';
    const { signal } = this.#ac;

    const results = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { signal });
          return response.ok ? await response.json() : null;
        } catch {
          return null;
        }
      })
    );

    const products = results.filter(Boolean);
    if (products.length === 0 || signal.aborted) return;

    list.replaceChildren(...products.map((product) => this.#card(product)));
    this.hidden = false;
  }

  #card(product) {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = product.url;
    link.className =
      'group group/zoom flex flex-col gap-2 rounded-card focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent';

    const media = document.createElement('span');
    media.className = 'block aspect-square overflow-hidden rounded-card border border-border bg-surface-secondary';
    if (product.featured_image) {
      const img = document.createElement('img');
      const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '500');
      img.src = url.toString();
      img.alt = product.title;
      img.loading = 'lazy';
      img.width = 500;
      img.height = 500;
      img.className = 'size-full object-cover motion-safe:transition-transform motion-safe:duration-500 motion-safe:ease-out motion-safe:group-hover/zoom:scale-105 motion-safe:group-active/zoom:scale-105';
      media.append(img);
    }

    const title = document.createElement('span');
    title.className = 'line-clamp-2 text-sm font-medium text-on-surface';
    title.textContent = product.title;

    const price = document.createElement('span');
    price.className = 'flex flex-wrap items-baseline gap-2 text-sm font-semibold text-on-surface';
    const current = document.createElement('bdi');
    current.textContent = formatMoney(product.price, this.dataset.moneyFormat);
    price.append(current);
    if (product.compare_at_price && product.compare_at_price > product.price) {
      const compare = document.createElement('s');
      compare.className = 'text-xs font-normal text-compare';
      compare.textContent = formatMoney(product.compare_at_price, this.dataset.moneyFormat);
      price.append(compare);
    }

    link.append(media, title, price);
    item.append(link);
    return item;
  }
}

if (!customElements.get('s-recently-viewed')) {
  customElements.define('s-recently-viewed', SRecentlyViewed);
}
